import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Edit, Trash2, Plus,
} from 'lucide-react';
import {
    PageHeader, ListHeader, Pagination,
    FilterGroup, FilterSelect, SearchBar, DateRangeFilter,
    Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Button
} from '@/components/shared/ui';
import AlertDialog from '@/components/shared/ui/AlertDialog';
import { Scenario } from '../../types/admin';
import { useScenarioAdminList, useDeleteScenario } from '@/hooks/useScenario';
import { ApiError } from '@/services/apiClient';
import {
    convertScenarioAdminDtoToScenario,
    extractScenarioListCategoryRows,
    extractScenarioListDeviceTypeRows,
    resolveDeviceTypeIdForScenarioListPlatform,
    type ScenarioListPlatformFilterKey,
} from '@/utils/scenarioUtils';
import { getScenarioCategoryList, getDeviceTypeList } from '@/services/scenarioService';
import { ROUTES } from '../../lib/constants/routes';
import { useAdminPageTitle } from '@/pages/manager/AdminManagerShell';
import { formatLocaleInteger } from '@/lib/localeFormat';

const SQ_PAGE = 'page';
const SQ_Q = 'q';
const SQ_CAT = 'cat';
const SQ_PLAT = 'plat';
const SQ_FROM = 'from';
const SQ_TO = 'to';

function isPlatformListKey(v: string | null): v is ScenarioListPlatformFilterKey {
    return v === 'VR' || v === 'MOBILE' || v === 'PC';
}

function pickApiErrorUserMessage(data: unknown): string {
    if (data == null || typeof data !== 'object') return '';
    const o = data as { message?: unknown; error?: unknown };
    if (typeof o.message === 'string' && o.message.trim()) return o.message.trim();
    if (Array.isArray(o.message) && o.message.length > 0) {
        const parts = o.message.filter(
            (x): x is string => typeof x === 'string' && x.trim().length > 0
        );
        if (parts.length) return parts.join('\n');
    }
    if (typeof o.error === 'string' && o.error.trim()) return o.error.trim();
    return '';
}

export interface AdminScenarioManagementProps {
    hideBreadcrumb?: boolean;
}

export default function AdminScenarioManagement(props?: AdminScenarioManagementProps): React.ReactElement {
    const { t, i18n } = useTranslation('scenario');
    const { hideBreadcrumb = false } = props ?? {};
    const navigate = useNavigate();
    const { pathname, search: locationSearch } = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const {
        listPage: currentPage,
        searchQuery,
        categoryFilter,
        platformFilter,
        dateRange,
    } = useMemo(() => {
        const p = searchParams;
        const page = Math.max(1, parseInt(p.get(SQ_PAGE) || '1', 10) || 1);
        const rawPlat = p.get(SQ_PLAT);
        const platform: ScenarioListPlatformFilterKey | 'all' = isPlatformListKey(rawPlat) ? rawPlat : 'all';
        return {
            listPage: page,
            searchQuery: p.get(SQ_Q) || '',
            categoryFilter: p.get(SQ_CAT) || 'all',
            platformFilter: platform,
            dateRange: {
                start: p.get(SQ_FROM) || null,
                end: p.get(SQ_TO) || null,
            } as { start: string | null; end: string | null },
        };
    }, [searchParams]);
    const basePath = '/admin';
    const adminPageTitle = useAdminPageTitle();

    const updateListQuery = useCallback(
        (mutate: (n: URLSearchParams) => void, resetPage: boolean) => {
            const n = new URLSearchParams(searchParams);
            mutate(n);
            if (resetPage) n.delete(SQ_PAGE);
            setSearchParams(n, { replace: true });
        },
        [searchParams, setSearchParams]
    );

    /** URL의 q와 별도로 두어 매 키마다 setSearchParams로 리렌더되지 않게 함 (한글 IME 조합 유지). */
    const [searchInput, setSearchInput] = useState(searchQuery);
    useEffect(() => {
        setSearchInput(searchQuery);
    }, [searchQuery]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (searchInput === searchQuery) return;
            updateListQuery(
                (n) => {
                    if (searchInput.trim()) n.set(SQ_Q, searchInput);
                    else n.delete(SQ_Q);
                },
                true
            );
        }, 400);
        return () => clearTimeout(t);
    }, [searchInput, searchQuery, updateListQuery]);

    const [scenarios, setScenarios] = useState<Scenario[]>([]);
    const [filteredScenarios, setFilteredScenarios] = useState<Scenario[]>([]);

    const [alertState, setAlertState] = useState({ isOpen: false, message: '' });
    const showAlert = (message: string) => setAlertState({ isOpen: true, message });
    const closeAlert = () => setAlertState({ isOpen: false, message: '' });

    const itemsPerPage = 20;

    const [categoryRows, setCategoryRows] = useState<{ id: number; title: string }[]>([]);
    const [deviceRows, setDeviceRows] = useState<{ id: number; title: string }[]>([]);
    const dateRangeInvalid = Boolean(
        dateRange.start && dateRange.end && dateRange.start > dateRange.end
    );

    const deleteScenarioMutation = useDeleteScenario();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [catRes, devRes] = await Promise.all([
                    getScenarioCategoryList(undefined, i18n.language),
                    getDeviceTypeList(),
                ]);
                if (cancelled) return;
                setCategoryRows(extractScenarioListCategoryRows(catRes));
                setDeviceRows(extractScenarioListDeviceTypeRows(devRes));
            } catch {
                if (!cancelled) {
                    setCategoryRows([]);
                    setDeviceRows([]);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const platformDeviceTypeIdForApi = useMemo(() => {
        if (platformFilter === 'all') return undefined;
        const id = resolveDeviceTypeIdForScenarioListPlatform(deviceRows, platformFilter);
        return id != null ? String(id) : undefined;
    }, [deviceRows, platformFilter]);

    const {
        data: apiData,
        isLoading,
        error,
    } = useScenarioAdminList(
        {
            page: currentPage,
            pageSize: itemsPerPage,
            search: searchQuery || undefined,
            startDate: dateRange.start || undefined,
            endDate: dateRange.end || undefined,
            category: categoryFilter !== 'all' ? categoryFilter : undefined,
            platform: platformDeviceTypeIdForApi,
        },
        {
            enabled: currentPage > 0 && itemsPerPage > 0,
            staleTime: 0,
            refetchOnMount: true,
        }
    );

    useEffect(() => {
        setFilteredScenarios(scenarios);
    }, [scenarios]);

    useEffect(() => {
        if (apiData != null) {
            const list = apiData.scenarioList ?? [];
            setScenarios(list.map(convertScenarioAdminDtoToScenario));
            return;
        }
        if (error && !isLoading) {
            setScenarios([]);
        }
    }, [apiData, error, isLoading]);

    const scenarioTotalCount = apiData?.totalCount ?? filteredScenarios.length;

    const { totalPages, paginatedItems } = useMemo(() => {
        const pages = Math.max(1, Math.ceil(scenarioTotalCount / itemsPerPage));
        const items = apiData != null
            ? filteredScenarios
            : filteredScenarios.slice(
                (currentPage - 1) * itemsPerPage,
                currentPage * itemsPerPage
            );
        return { totalPages: pages, paginatedItems: items };
    }, [apiData, scenarioTotalCount, filteredScenarios, currentPage, itemsPerPage]);

    const categoryOptions = useMemo(
        () => [
            { value: 'all', label: t('management.filterCategoryAll') },
            ...categoryRows.map((c) => ({ value: String(c.id), label: c.title })),
        ],
        [categoryRows, t]
    );

    const platformOptions = useMemo(
        () => [
            { value: 'all', label: t('management.filterPlatformAll') },
            { value: 'VR', label: 'VR' },
            { value: 'MOBILE', label: 'Mobile' },
            { value: 'PC', label: 'PC' },
        ],
        [t]
    );

    const onPlatformFilterChange = useCallback(
        (val: string) => {
            if (val === 'all' || val === 'VR' || val === 'MOBILE' || val === 'PC') {
                updateListQuery(
                    (n) => {
                        if (val === 'all') n.delete(SQ_PLAT);
                        else n.set(SQ_PLAT, val);
                    },
                    true
                );
            }
        },
        [updateListQuery]
    );

    const formatPlatform = (platformString: unknown) => {
        if (!platformString || platformString === '-') return '-';
        return String(platformString)
            .split(',')
            .map((p) => p.trim().toUpperCase())
            .map((p) => (p === 'MOBILE' ? 'Mobile' : p))
            .join(', ');
    };

    const handleEditScenario = (scenarioId: string | number, listTitle: string) => {
        const returnTo = `${pathname}${locationSearch}`;
        navigate(ROUTES.ADMIN.SCENARIO_EDIT(scenarioId), {
            state: { adminListTitle: listTitle, scenariosListReturn: returnTo },
        });
    };

    const handleDeleteScenario = (scenarioId: string | number, title: string) => {
        if (!window.confirm(t('management.deleteConfirmWithTitle', { title }))) return;

        const id = Number(scenarioId);
        if (!Number.isFinite(id) || id <= 0) {
            showAlert(t('management.invalidScenarioId'));
            return;
        }

        deleteScenarioMutation.mutate(id, {
            onSuccess: () => showAlert(t('management.deleteSuccess')),
            onError: (err) => {
                if (err instanceof ApiError) {
                    const bodyMsg = pickApiErrorUserMessage(err.data);
                    if (err.status === 403) {
                        showAlert(bodyMsg || t('management.deleteForbidden'));
                    } else if (err.status === 404) {
                        showAlert(bodyMsg || t('management.deleteNotFound'));
                    } else if (err.status === 409) {
                        showAlert(bodyMsg || err.message || t('management.deleteConflict'));
                    } else {
                        showAlert(
                            bodyMsg || err.message || t('management.deleteFailedWithStatus', { status: err.status })
                        );
                    }
                    return;
                }
                showAlert(err instanceof Error ? err.message : t('management.deleteFailedGeneric'));
            },
        });
    };

    return (
        <>
            <PageHeader
                title={adminPageTitle ?? t('management.title')}
                titleSize="admin"
                breadcrumbs={hideBreadcrumb ? [] : [{ label: t('management.title') }]}
                actions={
                    <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-4">
                            <Button
                                onClick={() => navigate(ROUTES.ADMIN.SCENARIO_ADD)}
                                className="bg-brand-500 hover:bg-brand-600 text-white"
                            >
                                <Plus size={18} className="mr-2" />
                                {t('management.addExistingScenario')}
                            </Button>
                            <DateRangeFilter
                                startDate={dateRange.start || ''}
                                endDate={dateRange.end || ''}
                                onStartDateChange={(val) => {
                                    updateListQuery(
                                        (n) => {
                                            if (val) n.set(SQ_FROM, val);
                                            else n.delete(SQ_FROM);
                                        },
                                        true
                                    );
                                }}
                                onEndDateChange={(val) => {
                                    updateListQuery(
                                        (n) => {
                                            if (val) n.set(SQ_TO, val);
                                            else n.delete(SQ_TO);
                                        },
                                        true
                                    );
                                }}
                            />
                        </div>
                        {dateRangeInvalid && (
                            <p className="text-sm text-amber-600" role="alert">
                                {t('management.dateRangeInvalid')}
                            </p>
                        )}
                    </div>
                }
            />

            {isLoading && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">{t('management.loading')}</p>
                </div>
            )}

            {error != null && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
                    <p className="text-sm font-medium text-red-800">{t('management.listLoadError')}</p>
                    <p className="text-sm text-red-700 mt-1">
                        {error instanceof Error ? error.message : String(error)}
                    </p>
                </div>
            )}

            <ListHeader
                totalCount={scenarioTotalCount}
                rightContent={
                    <FilterGroup>
                        <FilterSelect
                            options={categoryOptions}
                            value={categoryFilter}
                            onValueChange={(val) => {
                                updateListQuery(
                                    (n) => {
                                        if (val && val !== 'all') n.set(SQ_CAT, val);
                                        else n.delete(SQ_CAT);
                                    },
                                    true
                                );
                            }}
                        />
                        <FilterSelect
                            options={platformOptions}
                            value={platformFilter}
                            onValueChange={onPlatformFilterChange}
                        />
                        <SearchBar
                            placeholder={t('management.searchPlaceholder')}
                            value={searchInput}
                            onChange={(e) => {
                                setSearchInput(e.target.value);
                            }}
                        />
                    </FilterGroup>
                }
            />

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50/50">
                            <TableHead className="w-[60px]">{t('management.tableNo')}</TableHead>
                            <TableHead>{t('management.tableCategory')}</TableHead>
                            <TableHead>{t('management.tableTitle')}</TableHead>
                            <TableHead>{t('management.tablePlatform')}</TableHead>
                            <TableHead className="text-right">{t('management.tableSimulations')}</TableHead>
                            <TableHead>{t('management.tableRegisteredAt')}</TableHead>
                            <TableHead className="text-center w-[120px]">{t('management.tableAction')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {paginatedItems.map((item, index) => {
                            const globalIndex = (currentPage - 1) * itemsPerPage + index;
                            const rowNo = scenarioTotalCount - globalIndex;
                            return (
                                <TableRow key={item.id} className="hover:bg-gray-50 transition-colors text-base">
                                    <TableCell>{rowNo}</TableCell>
                                    <TableCell>
                                        <span className="text-gray-900">{item.category}</span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div
                                                className="font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
                                                onClick={() => navigate(`${basePath}/scenarios/${item.id}`)}
                                            >
                                                {item.title}
                                            </div>
                                            {item.modeValueTitle && (
                                                <span className="inline-flex items-center px-[3px] py-[1px] rounded text-[11px] font-medium bg-sky-50 text-sky-700 border border-sky-300 leading-tight">
                                                    {item.modeValueTitle}
                                                </span>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell>{formatPlatform(item.platform)}</TableCell>
                                    <TableCell className="text-right">
                                        {formatLocaleInteger(item.simulationCount ?? 0, i18n.language)}
                                    </TableCell>
                                    <TableCell>{item.createdDate || '-'}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center justify-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => { e.stopPropagation(); handleEditScenario(item.id, item.title); }}
                                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                                aria-label={t('management.editAria', { title: item.title })}
                                            >
                                                <Edit size={16} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                disabled={deleteScenarioMutation.isPending}
                                                onClick={(e) => { e.stopPropagation(); handleDeleteScenario(item.id, item.title); }}
                                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                                aria-label={t('management.deleteAria', { title: item.title })}
                                            >
                                                <Trash2 size={16} />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {paginatedItems.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                                    {t('management.noData')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={scenarioTotalCount}
                    onPageChange={(p) => {
                        updateListQuery(
                            (n) => {
                                if (p > 1) n.set(SQ_PAGE, String(p));
                                else n.delete(SQ_PAGE);
                            },
                            false
                        );
                    }}
                    itemsPerPage={itemsPerPage}
                />
            )}

            <AlertDialog
                isOpen={alertState.isOpen}
                onClose={closeAlert}
                message={alertState.message}
            />
        </>
    );
}
